/**
 * Dialog — shadcn/ui primitive (TruSpace adoption, 2026-05-22).
 *
 * Radix-backed modal dialog with the proper shadcn entrance
 * choreography via `tw-animate-css`. Out of the box you get focus
 * trap, ESC dismiss, click-outside dismiss, scroll lock, and full
 * `aria-modal` / `aria-labelledby` wiring — none of which you have
 * to maintain yourself.
 *
 * Bundle cost: `@radix-ui/react-dialog` ≈ 15 KB gz including its
 * primitive deps. `tw-animate-css` is ≈ 2 KB CSS-only and provides
 * the animation utilities for every shadcn primitive.
 *
 * All animations stay on `transform` + `opacity` only
 * (compositor-safe, satisfies motion.mdc §3).
 *
 * Anatomy:
 *   <Dialog>           — root, controls open state
 *     <DialogTrigger>  — the button that opens the dialog
 *     <DialogContent>  — the modal surface
 *       <DialogHeader>
 *         <DialogTitle>           (announced by SR on open)
 *         <DialogDescription>     (announced after the title)
 *       </DialogHeader>
 *       …body…
 *       <DialogFooter>            (right-aligned action buttons)
 *     </DialogContent>
 *   </Dialog>
 *
 * @see https://ui.shadcn.com/docs/components/dialog
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type {
  ComponentPropsWithoutRef,
  HTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

// Radix primitives re-exported under shadcn-canonical names. These
// are component values, but `react-refresh/only-export-components`
// linter walks the AST and flags non-PascalCase identifiers it sees
// in the export list (the `DialogPrimitive.Foo` accesses confuse it).
// The disable below preserves the upstream shadcn shape; production
// builds and HMR for the actual component bodies below are
// unaffected.
/* eslint-disable react-refresh/only-export-components */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;
/* eslint-enable react-refresh/only-export-components */

export function DialogOverlay({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-50 bg-black/80',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

type DialogContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /**
   * Set false for a dialog the user must answer rather than dismiss (e.g. the
   * forced Terms + Privacy gate, which offers Accept or Decline instead).
   */
  showCloseButton?: boolean;
};

export function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 grid w-full min-w-0 max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border border-border bg-background p-6 shadow-lg duration-[var(--duration-base)] sm:rounded-lg',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          // shadcn slides from `top-[48%]`, an off-scale fraction of the
          // dialog's own height. A fixed 8px nudge reads the same and stays on
          // the spacing scale.
          'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity',
              'hover:opacity-100',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:pointer-events-none',
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-col space-y-1.5 text-center sm:text-left',
        className,
      )}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn(
        'text-lg font-semibold leading-none tracking-tight',
        className,
      )}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}
